import React, { useState, useEffect } from "react";
import Head from "next/head";
import Image from "next/image";
import IMAGES from "@/public/assets/img";
import GlobalBuyerForm from "@/component/shared/globalBuyerForm/globalBuyerForm";
import { useDownloadStrip } from "@/layout/downloadStripContext";
import loadable from "@loadable/component";
const AccordianFaq = loadable(() => import("@/component/shared/accordianfaq"));
import { useRouter } from "next/router";
import Link from "next/link";
import AOS from "aos";
import { getMetadata } from "@/component/shared/getMetaData";
import enIN from '@/locales/global-buyer/enIN';
import esMX from '@/locales/global-buyer/esMX';
import enUS from "@/locales/global-buyer/enUS";
import { generateFaqSchema } from "@/component/shared/faqSchema";
import { getServiceSchema } from "@/lib/serviceSchema";
import { pushCleverTapEvent } from "@/component/shared/pushCleverTapEvent";


const SliderAnimationBuyer = loadable(() =>
  import("@/component/shared/slideranimationBuyer")
);

const GlobalBuyer = () => {
  const { downloadStrip } = useDownloadStrip();
  const router = useRouter();
  const { locale } = router;
  const t = (locale === 'es-mx' && esMX) || (locale === 'en-us' && enUS) || (locale === 'en-in' && enIN);
  const [open, setOpen] = useState(0);
  const [mWidth, setMWidth] = useState(0);
  const metadata = getMetadata(t);
  const faqSchema = generateFaqSchema(t.Home_Faq);


  const toggle = (index) => {
    if (open === index) {
      return setOpen(null);
    }
    setOpen(index);
  };

  const handleWindowResize = () => {
    setMWidth(window.innerWidth);
  };
  useEffect(() => {
    AOS.init();
    handleWindowResize();
    window.addEventListener("load", handleWindowResize);
    // unsubscribe from the event on component unmount
     if(locale === 'en-in') {
        pushCleverTapEvent("Global_Buyer_Visited", {
            "PageName": 'Global_Buyer_Page',
          });}
    return () => window.removeEventListener("resize", handleWindowResize);
  }, [router.asPath]);

  //   Service Schema
    const schemaData = getServiceSchema({
        metaTitle:t.SeoContent.title,
        pageUrl: t.SeoContent.url,
        websiteLogo:IMAGES.SHARED.credlixLogo.src,
        mainImageUrl:IMAGES.globalBuyer?.globalbyersHome?.src,
        metaDescription:t.SeoContent.description,
        pageName: t.SeoContent.title
    });
  return (
    <main>
      <Head>
              <title>{metadata.title}</title>
              <meta name="description" content={metadata.description} />
              <meta name="keywords" content={metadata.keywords} />
              <link rel="icon" href={metadata.icon} />
              <meta property="og:title" content={metadata.openGraph.title} />
              <meta property="og:description" content={metadata.openGraph.description} />
              <meta property="og:image" content={metadata.openGraph.images} />
              <meta property="og:url" content={metadata.openGraph.url} />
              <link rel="canonical" href={metadata.alternates.canonical} />

              <link rel="preload" as="image" href={IMAGES.globalBuyer?.globalbyersHome?.src}/>
              <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                    __html: JSON.stringify(faqSchema)
                    }}
              />
              <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
          />
          </Head>
      
        <section className={`${downloadStrip ? (t.BannerTopMargin ? "mt-[75px] sm:mt-28" : "") : (t.BannerTopMargin ? "mt-28 sm:mt-28" : "mt-16 sm:mt-20")} relative bg-[#E9F3FD]`}
          >
          <Image src={IMAGES.globalBuyer?.globalbuyersNew?.src} width={685} height={449} priority={true} fetchPriority="high" alt="image" className="absolute left-1/2 top-1/2 -translate-x-[40%] -translate-y-[45%] mx-auto sm:hidden"/>
          <div className="container w-full z-[1] relative sm:px-3">
            <div className="flex justify-between items-center sm:flex-col">
            <div className="w-[30%] sm:w-full sm:pt-6">
               <p className="text-brand text-base2 font-medium mb-xsp sm:text-sm">{t.HomeNewData.title}</p>
                <h1 className="text-transparent bg-clip-text bg-gradient-to-r from-[#022D60] to-[#0363C4] mb-3 sm:mb-xsp" dangerouslySetInnerHTML={{ __html:(t.HomeNewData.mainHeading) }} />
              <div className=" text-[#022B5C] text-textxl font-semibold pr-1" dangerouslySetInnerHTML={{ __html:(t.HomeNewData.text) }}></div>
            </div>
            <div className="w-2/5	sm:w-full my-4">
              <GlobalBuyerForm />
            </div>
            </div>
          </div>
        </section>
        <SliderAnimationBuyer titles={t.posts} content={t.sliderBuyer} heading={t.sliderMainHeading} />

        <section className="container">
          <h2 className="text-xxxl text-secondary sm:text-2xl font-bold text-center my-20 sm:mt-16 ">
           {t.BenefitsHeading.heading} 
          </h2>
          <div className="flex justify-center gap-6 sm:w-full sm:flex-col sm:gap-y-14">
            <div className="relative">
                <Image
                  src={IMAGES?.globalBuyer?.BenefitsBuyerImage}
                  width={600}
                  height={382}
                  quality={100}
                  alt="benefitsBuyers"
                  className="w-auto h-auto xl:w-auto lg:w-[550px] sm:h-[270px]"
                />
              <div className="flex flex-col md:flex-row justify-between items-start gap-6 absolute top-0">
                {t.sections.map((section, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row items-end p-4 sm:p-0 rounded-lg"
                  >
                    {/* Text Section */}
                    <div
                      className={`flex flex-col items-start py-6 xxl:p-1 xl:p-1 lg:p-2 md:1 sm:py-2 px-4 sm:px-1 w-3/5 md:w-1/2`}
                    >
                      <h2 className="text-base sm:text-sm font-bold mb-2 sm:mb-1 text-secondary">
                        {section.title}
                      </h2>
                      <ul>
                        {section.items.map((item, itemIndex) => (
                          <li
                            key={itemIndex}
                            className="flex items-start mb-3 sm:mb-1"
                          >
                            <span className="text-green-500 w-3 h-3 sm:w-2 contents text-base mr-2">
                              <Image
                                className="w-3 h-3 sm:w-2"
                                src={item.icon}
                                alt="icon-img"
                                width={20}
                                height={20}
                                
                              />
                            </span>
                            <div className="ml-1">
                              <span className="font-bold text-base sm:text-xs">
                                {item.title}:
                              </span>
                              <span className="ml-1 text-base sm:text-xs font-normal">
                                {item.description}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
                <Image
                  src={IMAGES?.globalBuyer?.BenefitsSupplier}
                  width={600}
                  height={382}
                  quality={100}
                  alt="benefitsBuyers"
                  className="w-auto h-auto xl:w-auto lg:w-[550px] sm:h-[280px]"
                />
              <div className="flex flex-col md:flex-row justify-between items-start gap-6 absolute top-0">
                {t.sections2.map((section, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row p-2 sm:p-1 rounded-lg"
                  >
                    {/* Text Section */}
                    <div
                      className={`flex flex-col items-start p-2 xxl:p-1 xl:p-1 lg:p-2 md:1 sm:p-0.5 sm:pr-1 w-[65%] md:w-1/2 sm:w-[70%]`}
                    >
                      <h2 className="text-base font-bold mb-2 sm:mb-1 text-secondary">
                        {section.title}
                      </h2>
                      <ul>
                        {section.items.map((item, itemIndex) => (
                          <li
                            key={itemIndex}
                            className="flex items-start mb-1 sm:mb-0"
                          >
                            <span className="text-green-500 text-sm sm:text-sm mr-2 w-3 h-3 contents">
                              <Image
                                className="w-3 h-3 sm:w-2"
                                src={item.icon}
                                alt="icon-img"
                                width={20}
                                height={20}
                                
                              />
                            </span>
                            <div className="ml-1">
                              <span className="font-bold text-base sm:text-xs">
                                {item.title}:
                              </span>
                              <span className="ml-1 text-base font-normal sm:text-xs">
                                {item.description}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
        <section className="relative ">
          <h2 className="text-xxxl sm:text-2xl text-secondary font-bold text-center mb-10 mt-28 sm:mt-10 sm:px-3">
            {t.Dashboard.mainHeading}
          </h2>
          <div className="bg-[#FFF6D3] absolute bottom-0 h-[370px] sm:h-[150px] z-[1] w-full"></div>
          <div className="flex justify-center relative z-[2] sm:px-3">
            <Image
              src={t.Dashboard.imagePath}
              width={1000}
              height={550}
              quality={100}
              priority={true}
              alt="Dashboard"
              className="w-auto h-auto"
            />
          </div>
        </section>

        <section className="py-20 sm:py-[70px] md:py-[80px]">
            <div className="container px-3 lg:px-3 xm:px-2">
              <h2 className="text-xxxl text-black font-semibold text-center max-w-[60%] m-auto mb-3 sm:max-w-full sm:text-2xl">
                {t.solutionsSec.heading}
              </h2>

              <div className="flex flex-wrap justify-between mx-auto max-w-[1100px] mt-14">
                {t.solutionsSec.solutions?.map((item, i) => (
                  <Link
                    href={item.href}
                    rel="noopener noreferrer"
                    key={i}
                    className="w-[30%] md:w-[49%] sm:w-[100%] sm:mb-3 py-6 px-3 sm:pb-12 shadow-lg rounded-[15px] border border-[#E6E6E6] group"
                  >
                    <span className="rounded-[8px] flex items-center w-full">
                      <Image
                        
                        width={48}
                        height={48}
                        src={item.src}
                        alt={item.heading}
                        className="m-auto"
                      />
                    </span>
                    <h2 className="mt-5 mb-3 text-lg font-semibold text-[#2C2C2C] text-center">
                      {item.heading}
                    </h2>
                    <p className="text-base text-black font-regular md:min-h-[auto] sm:min-h-[auto] text-center">
                      {item.paragraph}
                    </p>
                    <div className="flex items-center text-base text-[#3c3c3c] font-medium transition duration-150 ease-in-out group-hover:text-[#1479FC] mt-2 justify-center">
                          <span className="mr-1">{item.learnmoreCta}</span>
                          <svg xmlns="https://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="brand" width="24" height="24" className="transition-transform duration-200 transform group-hover:translate-x-[20px] fill-current text-[#3c3c3c] group-hover:text-[#1479FC]">
                            <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"></path></svg>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
        </section>

        <section className="container py-20 sm:py-14">
          {mWidth > 767 ? (
              <Image
                src={t.BuyerPayment.imagePath}
                width={1080}
                height={548}
                alt="arrow"
                quality={100}
                className="m-auto block"
              />
          ) : (
              <Image
                src={t.BuyerPayment.imagePathMob}
                width={1000}
                height={500}
                alt="arrow"
                quality={100}
              />
          )}
        </section>

        <section className="py-20 sm:py-14" style={{
            backgroundImage: `url(${IMAGES?.globalBuyer?.globalMask.src})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center center"
        }}>
           
            <div className="container sm:relative">
              <div className="flex flex-wrap">
                <div className="w-[60%] sm:w-full"></div>
                <div className="w-[38%] sm:w-full">
                <GlobalBuyerForm />
                </div>
              </div>
              
            
          </div>
        </section>
        {t.Home_Faq &&
        <section className="bg-white py-20 sm:py-[70px]">
          <div className="container px-3 xm:px-2 ">
            <div>
              <h2 className="mb-6 font-bold text-center text-heading3 text-secondary">
                {t.Faq_heading}
              </h2>
            </div>
            <div className="w-full text-left bg-white">
              <div className="container sm:p-0">
                <AccordianFaq faqData={t.Home_Faq} />
              </div>
            </div>
          </div>
        </section>}
      
    </main>
  );
};

export default GlobalBuyer;
